<template>
  <section class="cfo-panel section-card" :class="scope.clsPrefix" v-loading="loading">
    <div class="section-card-title">企业核心经营指标</div>
    <div v-if="loading" class="loading-state">加载中...</div>
    <div v-else-if="error" class="error-state">数据加载失败，请稍后重试</div>
    <div v-else class="indicators-container">
      <div
        v-for="(item, index) in indicators"
        :key="index"
        class="indicator-item"
        @mouseenter="hoverIndex = index"
        @mouseleave="hoverIndex = -1"
      >
        <div class="indicator-header">
          <span class="indicator-name">{{ item.name }}</span>
          <span :class="['indicator-trend', item.trendClass]">
            <span class="trend-value">{{ item.trendValue }}</span>
            <span v-if="item.trendIcon" class="trend-icon">{{ item.trendIcon }}</span>
          </span>
        </div>
        <div class="indicator-value">
          <span class="value-num">{{ item.value }}</span>
          <span v-if="item.unit" class="value-unit">{{ item.unit }}</span>
        </div>
        <div class="indicator-mask" v-show="hoverIndex === index">
          <button
            type="button"
            class="smart-analysis-btn"
            @click.stop="$emit('smart-analysis', item)"
          >智能分析</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { fetchKpiCards, unwrapDashboardResponse } from '@/api/dashboard'

/**
 * 百分比类KPI列表（API返回值为小数，需转为百分数显示）
 */
const PCT_KPIS = ['销售毛利率', '费用率', '管理利润率', '现金回款']

/**
 * 金额类KPI列表（API返回值为万元，需转为亿元显示）
 */
const MONETARY_KPIS = ['经营利润', '销售收入']

export default {
  name: 'CfoCoreIndicators',
  data() {
    return {
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-cfo-core-indicators'
      },
      hoverIndex: -1,
      indicators: [],
      loading: false,
      error: false
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    /**
     * 加载核心经营指标数据
     */
    async loadData() {
      this.loading = true
      this.error = false
      try {
        const res = await fetchKpiCards()
        const rawData = unwrapDashboardResponse(res)
        this.indicators = this.formatIndicators(rawData)
      } catch (e) {
        console.error('获取核心经营指标失败:', e)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    /**
     * 将API返回的指标数据格式化展示
     * @param {Array} data API返回的KPI数组
     * @returns {Array} 格式化后的指标列表
     */
    formatIndicators(data) {
      if (!Array.isArray(data)) return []
      return data.map((item) => {
        const { kpi_name, current_val, target_val, severity } = item
        let value = ''
        let unit = ''
        let trendValue = '暂无目标值'
        let trendClass = 'trend-flat'
        let trendIcon = ''
        let sub = '暂无目标值'

        if (PCT_KPIS.includes(kpi_name)) {
          value = (current_val * 100).toFixed(1)
          unit = '%'
        } else if (MONETARY_KPIS.includes(kpi_name)) {
          value = (current_val / 10000).toFixed(2)
          unit = '亿'
        } else {
          value = (current_val * 100).toFixed(0)
          unit = '%'
        }

        if (target_val != null) {
          let targetDisplay = ''
          let gapDisplay = ''
          const gap = current_val - target_val

          if (PCT_KPIS.includes(kpi_name) || kpi_name === '预算准确率' || kpi_name === '决策闭环率') {
            targetDisplay = (target_val * 100).toFixed(1) + '%'
            gapDisplay = Math.abs(gap * 100).toFixed(1) + 'pct'
          } else if (MONETARY_KPIS.includes(kpi_name)) {
            targetDisplay = (target_val / 10000).toFixed(2) + '亿'
            gapDisplay = Math.abs(gap / 10000).toFixed(2) + '亿'
          } else {
            targetDisplay = target_val.toString()
            gapDisplay = Math.abs(gap).toString()
          }

          if (gap > 0) {
            trendValue = gapDisplay
            trendClass = 'trend-up'
            trendIcon = '↑'
            sub = `目标${targetDisplay}，▲${gapDisplay}`
          } else if (gap < 0) {
            trendValue = gapDisplay
            trendClass = 'trend-down'
            trendIcon = '↓'
            sub = `目标${targetDisplay}，▼${gapDisplay}`
          } else {
            trendValue = '持平'
            trendClass = 'trend-flat'
            trendIcon = '→'
            sub = `目标${targetDisplay}，持平`
          }
        }

        return {
          name: kpi_name,
          value,
          unit,
          trendValue,
          trendClass,
          trendIcon,
          sub,
          alertClass: severity || 'info'
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.rvc-cosmoplat-workspace-cfo-core-indicators {
  flex-shrink: 0;
  padding: 16px;
  background-color: #fff;
  border-radius: 4px;
  box-sizing: border-box;

  .section-card-title {
    margin: 0 0 10px;
    font-size: 14px;
    font-weight: 600;
    color: #1e2226;
    line-height: 20px;
  }

  .loading-state,
  .error-state {
    padding: 20px;
    text-align: center;
    font-size: 12px;
    color: #94a3b8;
  }

  .error-state {
    color: #dc2626;
  }

  .indicators-container {
    display: flex;
    flex-wrap: nowrap;
    gap: 16px;
    overflow-x: auto;
    padding-bottom: 4px;
    scrollbar-width: thin;
    scrollbar-color: rgba(37, 99, 235, 0.28) transparent;
  }

  .indicators-container::-webkit-scrollbar {
    height: 4px;
  }

  .indicators-container::-webkit-scrollbar-thumb {
    background: rgba(37, 99, 235, 0.28);
    border-radius: 999px;
  }

  .indicator-item {
    flex: 1;
    flex-shrink: 0;
    min-width: 120px;
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    background: #eff2fc;
    padding: 12px 16px;
    position: relative;
  }

  .indicator-header {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 8px;
  }

  .indicator-name {
    font-size: 12px;
    font-family: PingFangSC-Medium;
    font-weight: 600;
    color: #333333;
  }

  .indicator-trend {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-family: PingFangSC-Regular;
    font-weight: 400;

    &.trend-up {
      color: #73b950;
    }

    &.trend-down {
      color: #f44f4f;
    }

    &.trend-flat {
      color: #f4b13d;
    }
  }

  .trend-icon {
    font-size: 12px;
    font-weight: 900;
  }

  .trend-value {
    font-weight: 500;
  }

  .indicator-value {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }

  .value-num {
    font-size: 24px;
    font-weight: 700;
    font-family: PingFangSC-Semibold;
    color: #333;
    line-height: 32px;
  }

  .value-unit {
    font-size: 12px;
    color: #333;
    font-weight: 400;
  }

  .indicator-mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #edf1fb3b;
    backdrop-filter: blur(1px);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .smart-analysis-btn {
    width: 64px;
    height: 24px;
    border: none;
    background-image: linear-gradient(270deg, #4a5ff3 0%, #4e86ff 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-family: PingFangSC-Regular;
    font-weight: 400;
    font-size: 12px;
    color: #ffffff;
    line-height: 12px;
  }
}

@media screen and (max-width: 1440px) {
  .rvc-cosmoplat-workspace-cfo-core-indicators {
    .value-num {
      font-size: 18px;
    }

    .indicator-name {
      font-size: 11px;
    }
  }
}
</style>
